// import { MDXEditorField } from '@/components/form-components/MDXEditorComponent'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { zodResolver } from '@hookform/resolvers/zod'
import '@mdxeditor/editor/style.css'
import { useCallback, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// Shadcn components
import { FormImagePreview } from '@/components/form-components/FormImagePreview'
import { SingleSelectComboboxField } from '@/components/form-components/SingleSelectCombobox'
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Textarea } from '@/components/shadcn/textarea'

// Form validation schema
const formSchema = z.object({
  problemStatement: z
    .string()
    .min(1, 'Problem statement is required')
    .max(512, 'Problem statement cannot exceed 512 characters'),
  keyQuestions: z.string().max(512, 'Key questions cannot exceed 512 characters').optional().nullable(),
  solutionDescription: z
    .string()
    .min(1, 'Solution description is required')
    .max(8000, 'Solution description cannot exceed 8000 characters'),
  differentiatedBenefit1: z.string().max(256, 'Benefit cannot exceed 256 characters').optional().nullable(),
  differentiatedBenefit1Pictogram: z.string().optional().nullable(),
  differentiatedBenefit2: z.string().max(256, 'Benefit cannot exceed 256 characters').optional().nullable(),
  differentiatedBenefit2Pictogram: z.string().optional().nullable(),
  differentiatedBenefit3: z.string().max(256, 'Benefit cannot exceed 256 characters').optional().nullable(),
  differentiatedBenefit3Pictogram: z.string().optional().nullable(),
  imageKey: z.string().optional().nullable(),
  keyContactId1: z.string().optional().nullable(),
  keyContactId2: z.string().optional().nullable(),
  keyContactId3: z.string().optional().nullable(),
  blurb: z.string().optional().nullable(),
})

export type AppAttributesForm = z.input<typeof formSchema>

type MessagesApplicationsApplication = RestSchemas['Messages.Applications.Application']
type MessagesApplicationsApplicationAttributes = RestSchemas['Messages.Applications.ApplicationAttributes']
type MessagesApplicationsPictogram = RestSchemas['Messages.Applications.Pictogram']
type MessagesApplicationsUser = RestSchemas['Messages.Applications.User']

type AppAttributesFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedApp: MessagesApplicationsApplication
  applicationAttribs: MessagesApplicationsApplicationAttributes
  pictograms: MessagesApplicationsPictogram[]
  users: MessagesApplicationsUser[]
}

export const AppAttributesFormV2 = (
  props: Omit<AppAttributesFormProps, 'applicationAttribs' | 'pictograms' | 'users'>
) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const {
    getApplicationsApiGetApplicationAttributes,
    getApplicationsApiGetPictograms,
    getApplicationsApiGetUsers,
    postApplicationsApiUpdateApplicationAttributes,
  } = useRestClient()

  const {
    data: applicationAttribs,
    isPending: applicationAttribsLoading,
    isError: applicationAttribsError,
  } = getApplicationsApiGetApplicationAttributes.useQuery({
    query: {
      applicationId: props.selectedApp.applicationId,
    },
  })

  const {
    data: pictograms,
    isPending: pictogramsLoading,
    isError: pictogramsError,
  } = getApplicationsApiGetPictograms.useQuery()

  const { data: users, isPending: usersLoading, isError: usersError } = getApplicationsApiGetUsers.useQuery()

  const { mutate: updateApplicationAttributes } = postApplicationsApiUpdateApplicationAttributes.useMutation(
    undefined,
    {
      onSuccess: () => {
        getApplicationsApiGetApplicationAttributes.invalidateQueries({})
        if (props.sheetTrigger) {
          setSheetOpen(false)
        }
        toast.success('Application attributes successfully updated')
        props.onSubmit?.()
      },
      onError: () => {
        toast.error('Error updating application attributes')
      },
    }
  )

  const submitHandler = useCallback(
    (data: AppAttributesForm) => {
      const updatedApp: MessagesApplicationsApplicationAttributes = {
        applicationId: props.selectedApp.applicationId,
        ...data,
        keyContactId1: data.keyContactId1 === '' ? null : Number(data.keyContactId1),
        keyContactId2: data.keyContactId2 === '' ? null : Number(data.keyContactId2),
        keyContactId3: data.keyContactId3 === '' ? null : Number(data.keyContactId3),
      }

      updateApplicationAttributes({
        body: updatedApp,
      })
      // console.log(updatedApp)
    },
    [props.selectedApp, updateApplicationAttributes]
  )

  if (applicationAttribsLoading || pictogramsLoading || usersLoading) return <FormComponentLoading />
  if (applicationAttribsError || pictogramsError || usersError) return <ErrorComponent />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent
        {...props}
        applicationAttribs={applicationAttribs}
        pictograms={pictograms}
        users={users}
        submitHandler={submitHandler}
      />
    </SheetWithFormComponent>
  ) : (
    <FormComponent
      {...props}
      applicationAttribs={applicationAttribs}
      pictograms={pictograms}
      users={users}
      submitHandler={submitHandler}
    />
  )
}

const FormComponent = (props: AppAttributesFormProps & { submitHandler: (data: AppAttributesForm) => void }) => {
  const defaultValues = useMemo(() => {
    const attribs = props.applicationAttribs
    return {
      problemStatement: attribs.problemStatement ?? '',
      keyQuestions: attribs.keyQuestions ?? '',
      solutionDescription: attribs.solutionDescription ?? '',
      differentiatedBenefit1: attribs.differentiatedBenefit1 ?? '',
      differentiatedBenefit1Pictogram: attribs.differentiatedBenefit1Pictogram ?? '',
      differentiatedBenefit2: attribs.differentiatedBenefit2 ?? '',
      differentiatedBenefit2Pictogram: attribs.differentiatedBenefit2Pictogram ?? '',
      differentiatedBenefit3: attribs.differentiatedBenefit3 ?? '',
      differentiatedBenefit3Pictogram: attribs.differentiatedBenefit3Pictogram ?? '',
      imageKey: attribs.imageKey ?? '',
      keyContactId1: attribs.keyContactId1?.toString() ?? '',
      keyContactId2: attribs.keyContactId2?.toString() ?? '',
      keyContactId3: attribs.keyContactId3?.toString() ?? '',
      blurb: attribs.blurb ?? '',
    }
  }, [props.applicationAttribs])

  const form = useForm<AppAttributesForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const watchImageKey = useWatch({ control: form.control, name: 'imageKey' })
  const watchPict1 = useWatch({ control: form.control, name: 'differentiatedBenefit1Pictogram' })
  const watchPict2 = useWatch({ control: form.control, name: 'differentiatedBenefit2Pictogram' })
  const watchPict3 = useWatch({ control: form.control, name: 'differentiatedBenefit3Pictogram' })

  const onSubmit = (data: AppAttributesForm) => {
    console.log(data)
    props.submitHandler(data)
  }

  return (
    <div>
      <Form {...form}>
        {/* <form onSubmit={form.handleSubmit(props.submitHandler)}> */}
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <SingleSelectComboboxField
                control={form.control}
                name="imageKey"
                label="Image"
                options={[
                  { value: '', label: 'None' },
                  ...props.pictograms.map((p) => ({
                    value: p.imageKey,
                    label: p.imageKey,
                  })),
                ]}
                placeholder="Select an image"
                allowClear={true}
              />
              <FormImagePreview imageKey={watchImageKey} />

              <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-8 pb-5">
                <SingleSelectComboboxField
                  control={form.control}
                  name="keyContactId1"
                  label="Key Contact User 1"
                  options={[
                    { value: '', label: 'None' },
                    ...props.users.map((user) => ({
                      value: user.id.toString(),
                      label: `${user.givenName} ${user.familyName}`,
                    })),
                  ]}
                  placeholder="Select a contact"
                  allowClear={true}
                />

                <SingleSelectComboboxField
                  control={form.control}
                  name="keyContactId2"
                  label="Key Contact User 2"
                  options={[
                    { value: '', label: 'None' },
                    ...props.users.map((user) => ({
                      value: user.id.toString(),
                      label: `${user.givenName} ${user.familyName}`,
                    })),
                  ]}
                  placeholder="Select a contact"
                  allowClear={true}
                />

                <SingleSelectComboboxField
                  control={form.control}
                  name="keyContactId3"
                  label="Key Contact User 3"
                  options={[
                    { value: '', label: 'None' },
                    ...props.users.map((user) => ({
                      value: user.id.toString(),
                      label: `${user.givenName} ${user.familyName}`,
                    })),
                  ]}
                  placeholder="Select a contact"
                  allowClear={true}
                />
              </div>

              {/* <div className="flex w-full flex-col gap-2 overflow-hidden rounded-xl">
                <MDXEditorField
                  control={form.control}
                  name="blurb"
                  label="Description"
                  placeholder="Describe the application in detail. I.E. Problem statement, key questions, solution description, etc..."
                />
              </div> */}

              <FormField
                control={form.control}
                name="problemStatement"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Problem Statement *</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="keyQuestions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Key Questions</FormLabel>
                    <FormControl>
                      <Textarea {...field} value={field.value || ''} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="solutionDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Solution Description *</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={6} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="differentiatedBenefit1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Differentiated Benefit 1</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <SingleSelectComboboxField
                control={form.control}
                name="differentiatedBenefit1Pictogram"
                label="Benefit 1 Pictogram"
                options={[
                  { value: '', label: 'None' },
                  ...props.pictograms.map((p) => ({
                    value: p.imageKey,
                    label: p.imageKey,
                  })),
                ]}
                placeholder="Select a pictogram"
                allowClear={true}
              />
              <FormImagePreview imageKey={watchPict1} />

              <FormField
                control={form.control}
                name="differentiatedBenefit2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Differentiated Benefit 2</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <SingleSelectComboboxField
                control={form.control}
                name="differentiatedBenefit2Pictogram"
                label="Benefit 2 Pictogram"
                options={[
                  { value: '', label: 'None' },
                  ...props.pictograms.map((p) => ({
                    value: p.imageKey,
                    label: p.imageKey,
                  })),
                ]}
                placeholder="Select a pictogram"
                allowClear={true}
              />
              <FormImagePreview imageKey={watchPict2} />

              <FormField
                control={form.control}
                name="differentiatedBenefit3"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Differentiated Benefit 3</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <SingleSelectComboboxField
                control={form.control}
                name="differentiatedBenefit3Pictogram"
                label="Benefit 3 Pictogram"
                options={[
                  { value: '', label: 'None' },
                  ...props.pictograms.map((p) => ({
                    value: p.imageKey,
                    label: p.imageKey,
                  })),
                ]}
                placeholder="Select a pictogram"
                allowClear={true}
              />
              <FormImagePreview imageKey={watchPict3} />
            </div>
            <div className="form-action-container">
              <Button type="submit">Submit</Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  )
}
